import type { User } from '@supabase/supabase-js';
import { AbstractViewModel, getAuthErrorMessage } from '@footsteps/shared';
import type { RegisterUserState } from './register-user.state';
import type {
  RegisterUserStep,
  RegisterUserProps,
  RegisterUserAuthMethods,
  RegisterUserFormErrors,
  RegisterUserFormFields,
} from './register-user.types';
import { loginEmailSchema, registrationSchema, otpSchema, profileSchema } from './register-user.schema';

const OTP_EXPIRY_SECONDS = 600; // 10 minutes
const RESEND_COOLDOWN_SECONDS = 60; // 1 minute

export class RegisterUserViewModel extends AbstractViewModel<RegisterUserState> {
  static initialState: RegisterUserState = {
    step: 'register',
    email: '',
    fullName: '',
    telephone: '',
    otp: '',
    isLoading: false,
    errors: {},
    apiError: null,
    resendCooldown: 0,
    otpTimeRemaining: 0,
    isEmailEditable: true,
  };

  private props: RegisterUserProps = {};

  private auth: RegisterUserAuthMethods | null = null;

  private isAnonymousUser = false;

  private isVerifiedUser = false;

  private cooldownInterval: ReturnType<typeof setInterval> | null = null;

  private expiryInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    super(RegisterUserViewModel.initialState);

    this.setEmail = this.setEmail.bind(this);
    this.setFullName = this.setFullName.bind(this);
    this.setTelephone = this.setTelephone.bind(this);
    this.setOtp = this.setOtp.bind(this);
    this.submitLoginEmail = this.submitLoginEmail.bind(this);
    this.submitRegister = this.submitRegister.bind(this);
    this.submitLoginOtp = this.submitLoginOtp.bind(this);
    this.submitOtp = this.submitOtp.bind(this);
    this.resendOtp = this.resendOtp.bind(this);
    this.resendLoginOtp = this.resendLoginOtp.bind(this);
    this.switchToLogin = this.switchToLogin.bind(this);
    this.switchToRegister = this.switchToRegister.bind(this);
  }

  setProps(props: RegisterUserProps): void {
    this.props = props;
  }

  setAuth(auth: RegisterUserAuthMethods): void {
    this.auth = auth;
  }

  determineStep(user: User | null): void {
    let step: RegisterUserStep;

    this.isAnonymousUser = false;
    this.isVerifiedUser = false;

    if (!user) {
      step = this.props.showLogin ? 'login' : 'register';
    } else if (user.is_anonymous && !user.new_email) {
      step = this.props.showLogin ? 'login' : 'register';
      this.isAnonymousUser = true;
    } else if (user.is_anonymous && user.new_email) {
      step = 'verify';
      this.isAnonymousUser = true;
    } else if (!user.is_anonymous && user.email_confirmed_at) {
      step = 'register';
      this.isVerifiedUser = true;
    } else {
      step = 'register';
    }

    const currentState = this.state.getValue();

    this.update((draft) => {
      draft.step = step;
      draft.isEmailEditable = !this.isVerifiedUser;
      draft.errors = {};
      draft.apiError = null;
    });

    if (step === 'verify' && user?.new_email && !currentState.email) {
      this.update((draft) => {
        draft.email = user.new_email!;
      });
    }

    if (this.isVerifiedUser && user) {
      this.update((draft) => {
        draft.fullName = (user.user_metadata?.full_name as string) ?? '';
        draft.telephone = (user.user_metadata?.telephone as string) ?? '';
        draft.email = user.email ?? '';
      });
    }
  }

  // ── Field setters ───────────────────────────────────────────────────

  setEmail(email: string): void {
    this.update((draft) => {
      draft.email = email;
      delete draft.errors.email;
      draft.apiError = null;
    });
  }

  setFullName(fullName: string): void {
    this.update((draft) => {
      draft.fullName = fullName;
      delete draft.errors.fullName;
      draft.apiError = null;
    });
  }

  setTelephone(telephone: string): void {
    this.update((draft) => {
      draft.telephone = telephone;
      delete draft.errors.telephone;
      draft.apiError = null;
    });
  }

  setOtp(otp: string): void {
    this.update((draft) => {
      draft.otp = otp;
      delete draft.errors.otp;
      draft.apiError = null;
    });
  }

  // ── Step switching ──────────────────────────────────────────────────

  switchToLogin(): void {
    this.update((draft) => {
      draft.step = 'login';
      draft.errors = {};
      draft.apiError = null;
    });
  }

  switchToRegister(): void {
    this.update((draft) => {
      draft.step = 'register';
      draft.errors = {};
      draft.apiError = null;
    });
  }

  // ── LoginForm submit (email only → send login OTP) ─────────────────

  async submitLoginEmail(): Promise<void> {
    if (!this.auth) return;

    const { email } = this.state.getValue();

    const parsed = loginEmailSchema.safeParse({ email });
    if (!parsed.success) {
      this.setFieldErrors(parsed.error.issues);
      return;
    }

    this.update((draft) => {
      draft.isLoading = true;
      draft.errors = {};
      draft.apiError = null;
    });

    try {
      await this.auth.sendLoginOtp(parsed.data.email);
    } catch (err) {
      this.update((draft) => {
        draft.apiError = getAuthErrorMessage(err, 'Kon inlogcode niet verzenden.');
        draft.isLoading = false;
      });
      return;
    }

    this.update((draft) => {
      draft.step = 'login-otp';
      draft.isLoading = false;
    });
    this.startCooldown();
    this.startExpiryTimer('login');
  }

  // ── RegisterForm submit (routes based on user state) ───────────────

  async submitRegister(): Promise<void> {
    if (!this.auth) return;

    if (this.isVerifiedUser) {
      await this.submitProfile();
      return;
    }

    if (this.isAnonymousUser) {
      await this.submitLinkEmail();
      return;
    }

    await this.submitCreateAccount();
  }

  // ── Login OTP verification ─────────────────────────────────────────

  async submitLoginOtp(): Promise<void> {
    if (!this.auth) return;

    const { email, otp } = this.state.getValue();

    const parsed = otpSchema.safeParse({ otp });
    if (!parsed.success) {
      this.setFieldErrors(parsed.error.issues);
      return;
    }

    this.update((draft) => {
      draft.isLoading = true;
      draft.errors = {};
      draft.apiError = null;
    });

    try {
      const { fullName, telephone } = this.state.getValue();
      const metadata: { fullName?: string; phone?: string } = {};
      if (fullName) metadata.fullName = fullName.trim();
      if (telephone) metadata.phone = telephone.trim();

      await this.auth.verifyLoginOtp(email, parsed.data.otp, Object.keys(metadata).length > 0 ? metadata : undefined);
      this.clearExpiryTimer();
      this.clearCooldown();

      this.update((draft) => {
        draft.isLoading = false;
      });
      this.props.onVerifySuccess?.();
    } catch (err) {
      this.update((draft) => {
        draft.apiError = getAuthErrorMessage(err, 'Ongeldige code. Probeer opnieuw.');
        draft.isLoading = false;
      });
    }
  }

  // ── Email OTP verification (for anon → link email flow) ────────────

  async submitOtp(): Promise<void> {
    if (!this.auth) return;

    const { email, otp } = this.state.getValue();

    const parsed = otpSchema.safeParse({ otp });
    if (!parsed.success) {
      this.setFieldErrors(parsed.error.issues);
      return;
    }

    this.update((draft) => {
      draft.isLoading = true;
      draft.errors = {};
      draft.apiError = null;
    });

    try {
      await this.auth.verifyEmailOtp(email, parsed.data.otp);
      this.clearExpiryTimer();
      this.clearCooldown();
      this.update((draft) => {
        draft.step = 'verified';
        draft.isLoading = false;
      });
      this.props.onVerifySuccess?.();
    } catch (err) {
      this.update((draft) => {
        draft.apiError = getAuthErrorMessage(err, 'Ongeldige code. Probeer opnieuw.');
        draft.isLoading = false;
      });
    }
  }

  async resendOtp(): Promise<void> {
    if (!this.auth) return;

    const { email, resendCooldown } = this.state.getValue();
    if (resendCooldown > 0) return;

    this.update((draft) => {
      draft.isLoading = true;
      draft.apiError = null;
    });

    try {
      await this.auth.resendEmailOtp(email);
      this.startCooldown();
      this.startExpiryTimer('register');
      this.update((draft) => {
        draft.isLoading = false;
        draft.otp = '';
      });
    } catch (err) {
      this.update((draft) => {
        draft.apiError = getAuthErrorMessage(err, 'Kon code niet opnieuw verzenden.');
        draft.isLoading = false;
      });
    }
  }

  async resendLoginOtp(): Promise<void> {
    if (!this.auth) return;

    const { email, resendCooldown } = this.state.getValue();
    if (resendCooldown > 0) return;

    this.update((draft) => {
      draft.isLoading = true;
      draft.apiError = null;
    });

    try {
      await this.auth.sendLoginOtp(email);
      this.startCooldown();
      this.startExpiryTimer('login');
      this.update((draft) => {
        draft.isLoading = false;
        draft.otp = '';
      });
    } catch (err) {
      this.update((draft) => {
        draft.apiError = getAuthErrorMessage(err, 'Kon code niet opnieuw verzenden.');
        draft.isLoading = false;
      });
    }
  }

  // ── Private: RegisterForm submit routes ─────────────────────────────

  private async submitProfile(): Promise<void> {
    if (!this.auth) return;

    const { fullName, telephone } = this.state.getValue();

    const parsed = profileSchema.safeParse({ fullName, telephone });
    if (!parsed.success) {
      this.setFieldErrors(parsed.error.issues);
      return;
    }

    this.update((draft) => {
      draft.isLoading = true;
      draft.errors = {};
      draft.apiError = null;
    });

    try {
      const data: Record<string, string> = { full_name: parsed.data.fullName };
      if (parsed.data.telephone) {
        data.telephone = parsed.data.telephone;
      }
      await this.auth.updateProfile(data);
      this.update((draft) => {
        draft.isLoading = false;
      });
    } catch (err) {
      this.update((draft) => {
        draft.apiError = getAuthErrorMessage(err, 'Kon profiel niet bijwerken.');
        draft.isLoading = false;
      });
    }
  }

  private async submitLinkEmail(): Promise<void> {
    if (!this.auth) return;

    const { email, fullName, telephone } = this.state.getValue();

    const parsed = registrationSchema.safeParse({ email, fullName, telephone });
    if (!parsed.success) {
      this.setFieldErrors(parsed.error.issues);
      return;
    }

    this.update((draft) => {
      draft.isLoading = true;
      draft.errors = {};
      draft.apiError = null;
    });

    try {
      const metadata: Record<string, string> = { full_name: parsed.data.fullName };
      if (parsed.data.telephone) {
        metadata.telephone = parsed.data.telephone;
      }
      await this.auth.linkEmail(parsed.data.email, metadata);
      this.update((draft) => {
        draft.step = 'verify';
        draft.isLoading = false;
      });
      this.startExpiryTimer('register');
    } catch (err) {
      this.update((draft) => {
        draft.apiError = getAuthErrorMessage(err);
        draft.isLoading = false;
      });
    }
  }

  private async submitCreateAccount(): Promise<void> {
    if (!this.auth) return;

    const { email, fullName, telephone } = this.state.getValue();

    const parsed = registrationSchema.safeParse({ email, fullName, telephone });
    if (!parsed.success) {
      this.setFieldErrors(parsed.error.issues);
      return;
    }

    this.update((draft) => {
      draft.isLoading = true;
      draft.errors = {};
      draft.apiError = null;
    });

    try {
      const metadata: Record<string, string> = { full_name: parsed.data.fullName };
      if (parsed.data.telephone) {
        metadata.telephone = parsed.data.telephone;
      }
      await this.auth.sendLoginOtp(parsed.data.email, { metadata, shouldCreateUser: true });
    } catch (err) {
      this.update((draft) => {
        draft.apiError = getAuthErrorMessage(err, 'Kon verificatiecode niet verzenden.');
        draft.isLoading = false;
      });
      return;
    }

    this.update((draft) => {
      draft.step = 'login-otp';
      draft.isLoading = false;
    });
    this.startCooldown();
    this.startExpiryTimer('register');
  }

  // ── Private helpers ──────────────────────────────────────────────────

  private setFieldErrors(issues: { path: (string | number)[]; message: string }[]): void {
    const fieldErrors: RegisterUserFormErrors = {};
    for (const issue of issues) {
      const field = issue.path[0] as keyof RegisterUserFormFields;
      if (!fieldErrors[field]) {
        fieldErrors[field] = issue.message;
      }
    }
    this.update((draft) => {
      draft.errors = fieldErrors;
    });
  }

  private startCooldown(): void {
    this.clearCooldown();
    this.update((draft) => {
      draft.resendCooldown = RESEND_COOLDOWN_SECONDS;
    });

    this.cooldownInterval = setInterval(() => {
      const { resendCooldown } = this.state.getValue();
      if (resendCooldown <= 1) {
        this.clearCooldown();
        return;
      }
      this.update((draft) => {
        draft.resendCooldown -= 1;
      });
    }, 1000);
  }

  private clearCooldown(): void {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
      this.cooldownInterval = null;
    }
    this.update((draft) => {
      draft.resendCooldown = 0;
    });
  }

  private startExpiryTimer(previousStep: 'login' | 'register'): void {
    this.clearExpiryTimer();
    this.update((draft) => {
      draft.otpTimeRemaining = OTP_EXPIRY_SECONDS;
    });

    this.expiryInterval = setInterval(() => {
      const { otpTimeRemaining } = this.state.getValue();
      if (otpTimeRemaining <= 1) {
        this.clearExpiryTimer();
        this.clearCooldown();
        this.update((draft) => {
          draft.step = previousStep;
          draft.otp = '';
          draft.otpTimeRemaining = 0;
          draft.apiError = 'Je verificatiecode is verlopen. Vraag een nieuwe code aan.';
        });
        return;
      }
      this.update((draft) => {
        draft.otpTimeRemaining -= 1;
      });
    }, 1000);
  }

  private clearExpiryTimer(): void {
    if (this.expiryInterval) {
      clearInterval(this.expiryInterval);
      this.expiryInterval = null;
    }
    this.update((draft) => {
      draft.otpTimeRemaining = 0;
    });
  }

  override dispose(): void {
    this.clearCooldown();
    this.clearExpiryTimer();
    super.dispose();
  }
}
